import React, { useState } from 'react';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: '',
    honeypot: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formStartTime] = useState(Date.now());

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre.trim() || formData.nombre.length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = 'Por favor, introduce un email válido';
    }
    
    if (!formData.asunto.trim() || formData.asunto.length < 5) {
      newErrors.asunto = 'El asunto debe tener al menos 5 caracteres';
    }
    
    if (!formData.mensaje.trim() || formData.mensaje.length < 10) {
      newErrors.mensaje = 'El mensaje debe tener al menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection: Honeypot check
    if (formData.honeypot) {
      return;
    }
    
    // Bot protection: Minimum time to fill form (3 seconds)
    const timeToFill = Date.now() - formStartTime;
    if (timeToFill < 3000) {
      setSubmitStatus({
        type: 'error',
        message: 'Por favor, espera unos segundos antes de enviar el formulario.'
      });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          asunto: '',
          mensaje: '',
          honeypot: ''
        });
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: data.message || 'Error al enviar el mensaje. Inténtalo de nuevo.' 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error de conexión. Por favor, inténtalo de nuevo más tarde.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="nombre" className="contact-form__label">
            Nombre completo *
          </label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className={`contact-form__input ${errors.nombre ? 'contact-form__input--error' : ''}`}
            placeholder="Tu nombre"
            required
          />
          {errors.nombre && <span className="contact-form__error">{errors.nombre}</span>}
        </div>

        <div className="contact-form__field">
          <label htmlFor="email" className="contact-form__label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`contact-form__input ${errors.email ? 'contact-form__input--error' : ''}`}
            placeholder="tu@email.com"
            required
          />
          {errors.email && <span className="contact-form__error">{errors.email}</span>}
        </div>
      </div>

      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="telefono" className="contact-form__label">
            Teléfono
          </label>
          <input
            type="tel"
            id="telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            className="contact-form__input"
            placeholder="+34 XXX XXX XXX"
          />
        </div>

        <div className="contact-form__field">
          <label htmlFor="asunto" className="contact-form__label">
            Asunto *
          </label>
          <input
            type="text"
            id="asunto"
            name="asunto"
            value={formData.asunto}
            onChange={handleChange}
            className={`contact-form__input ${errors.asunto ? 'contact-form__input--error' : ''}`}
            placeholder="¿Sobre qué nos quieres consultar?"
            required
          />
          {errors.asunto && <span className="contact-form__error">{errors.asunto}</span>}
        </div>
      </div>

      <div className="contact-form__field">
        <label htmlFor="mensaje" className="contact-form__label">
          Mensaje *
        </label>
        <textarea
          id="mensaje"
          name="mensaje"
          value={formData.mensaje}
          onChange={handleChange}
          className={`contact-form__textarea ${errors.mensaje ? 'contact-form__input--error' : ''}`}
          placeholder="Cuéntanos los detalles de tu consulta..."
          rows="5"
          required
        />
        {errors.mensaje && <span className="contact-form__error">{errors.mensaje}</span>}
      </div>

      <input
        type="text"
        name="honeypot"
        value={formData.honeypot}
        onChange={handleChange}
        style={{ display: 'none' }}
        tabIndex="-1"
        autoComplete="off"
      />

      {submitStatus && (
        <div className={`contact-form__status contact-form__status--${submitStatus.type}`}>
          {submitStatus.message}
        </div>
      )}

      <button
        type="submit"
        className="contact-form__submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="contact-form__spinner"></span>
            Enviando...
          </>
        ) : (
          'Enviar Mensaje'
        )}
      </button>
    </form>
  );
};

export default ContactForm;
